import { Observable } from 'rxjs';

export enum DispositionTypes {
  MISSED = 'MISSED',
  CANCELLED = 'CANCELLED',
  INITIATED = 'INITIATED',
}

export enum DirectionTypes {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
}

export type SessionTypes = 'SPARK' | 'BROADWORKS';

export enum CH_STORAGE_KEYS {
  CH_ERROR_LIST_MSG = 'chListErrorMsg',
}

export enum SESSION_TYPES {
  BROADWORKSCALLING = "BROADWORKSCALLING",
  MSTEAMS = "MSTEAMS", 
  SPARK = 'SPARK',
  UNKNOWN ="UNKNOWN",
  UCM = "UCM", 
  UCMC = "UCMC",
  WEBEXCALLING = "WEBEXCALLING",
  WEBEXCALLING_DI = "WEBEXCALLING_DI",
  WEBEXCALLING_SHARED = "WEBEXCALLING_SHARED"
}

export enum CALLING_SPECIFIC_REASON {
  CALLQUEUE = 'CALLQUEUE',
  HUNTGROUP = 'HUNTGROUP',
  BUSY = 'BUSY',
  NOANSWER = 'NOANSWER',
  UNAVAILABLE = 'UNAVAILABLE',
  UNCONDITIONAL = 'UNCONDITIONAL',
  TIMEOFDAY = 'TIMEOFDAY'
}
export interface ISDKCallHistoryRecord {
  id: string;
  url?: string;
  sessionId?: string;
  sessionType?: SessionTypes;
  startTime?: string;
  endTime?: string;
  durationSecs?: number;
  durationSeconds?: number;
  joinedDurationSeconds?: number;
  participantCount?: number;
  direction: DirectionTypes;
  disposition: DispositionTypes;
  self?: {
    id: string;
    name: string;
    phoneNumber?: string;
    incomingCallProtocols?: unknown[];
    ucmLineNumber?: number;
    managedById?: string; // Shared line support: GUID for WEBEXCALLING_SHARED
  };
  other: {
    id: string;
    name: string;
    phoneNumber?: string;
    isPrivate?: boolean;
    callbackAddress: string;
  };
  links: {
    callbackAddress: string;
    conversationUrl: string;
    locusUrl?: string;
  };
  isSelected?: boolean;
  isDeleted?: boolean;
  isPMR?: boolean;
  correlationIds?: unknown[];
}

export type ICallHistoryRecords = {
  lastUpdated?: string;
  items?: Record<string, ICallHistoryRecord>;
};

export interface ICallHistoryRecord {
  id: string;
  name: string;
  direction?: DirectionTypes;
  disposition?: DispositionTypes;
  startTime?: string;
  endTime?: string;
  sessionType?: string;
  sessionId?: string;
  phoneNumber?: string;
  callbackAddress?: string;
  isSelected?: boolean;
  durationSeconds?: number;
  callingSpecific?: string;
  lineNumber?: string; // Multiline support: Line label (e.g., 'L1', 'L2')
  managedByName?: string; // Multiline support: Name of the user who made the call on behalf of line owner
  isRead?: boolean;
  callingContainerIds?: string[];
  remotePartyId?: string; // Remote party user ID for presence subscriptions
}

export interface ICallHistoryAdapter {
  refresh(ID?: string, options?: CallHistoryFetchOptions): void;

  getAll(ID?: string, options?: CallHistoryFetchOptions): Observable<ICallHistoryRecord[]>;

  getOne?(ID?: string): Observable<ICallHistoryRecord>;
}

export enum SORT {
  ASC = 'ASC',
  DESC = 'DESC',
  DEFAULT = 'DESC',
}

export enum SORT_BY {
  END_TIME = 'endTime',
  DEFAULT = 'endTime',
  START_TIME = 'startTime',
}

export enum DATE {
  WEEK = 7,
  MONTH = 30,
  DEFAULT = 7,
}

export enum LIMIT {
  DEFAULT = 100,
  MAX = 200
}

export type CallHistoryFetchOptions = {
  /** 
   * Maximum number of records to request. Set to null to request without limit. 
   * Defaults to LIMIT.MAX (200).
   */
  limit?: number | null;
  /** 
   * Date range in days to request call history. 
   * Defaults to DATE.MONTH (30 days).
   */
  dateRange?: DATE;
  /** 
   * Optional ISO 8601 timestamp (e.g., "2025-10-10T10:00:00Z") to request records since that time.
   * The service will convert this to a dateRange (number of days), capped at DATE.MONTH (30 days).
   * This enables delta fetches to retrieve only recent call history records.
   * 
   * Note: Currently supported only by WebexCallHistoryAdapter. Other adapters accept the parameter
   * for interface compatibility but may not use it for delta fetch optimization.
   */
  since?: string;
}

export interface ICallbackInfo {
  callbackAddress: string;
  callbackType: string;
}

export interface ISelf {
  id: string;
  name: string;
  incomingCallProtocols: any[];
  callbackInfo: ICallbackInfo;
  ucmLineNumber?: number; // For WEBEXCALLING primary/virtual lines
  managedById?: string; // For WEBEXCALLING_SHARED lines (GUID)
}

export interface IOther {
  phoneNumber: string;
  id: string;
  name: string;
  isPrivate: boolean;
  callbackAddress: string;
}

export interface ILinks {
  locusUrl: string;
  callbackAddress: string;
}
export interface ICallingSpecifics {
  redirectionDetails?: {
    phoneNumber?: string;
    name?: string;
    reason: string;
    userId?: string;
    isPrivate: boolean;
    sipUrl?:string;
  };
  managedById?: string; // Shared line support: GUID for WEBEXCALLING_SHARED (nested location)
}

export interface IUserSession {
  id: string;
  durationSecs: number;
  self: ISelf;
  url: string;
  sessionId: string;
  sessionType: string;
  startTime: string;
  endTime: string;
  direction: string;
  disposition: string;
  other: IOther;
  durationSeconds: number;
  joinedDurationSeconds: number;
  participantCount: number;
  links: ILinks;
  isDeleted: boolean;
  isPMR: boolean;
  correlationIds: string[];
  isRead:boolean;
  callingSpecifics?: ICallingSpecifics;
  managedById?: string; // Shared line support: GUID for WEBEXCALLING_SHARED
  extensionPayload?: {
    callingContainerIds?: string[];
  };
}

export interface IUserSessionData {
  userSessions: IUserSession[];
}

export interface IWebexCallHistoryResponse {
  statusCode: number;
  data: IUserSessionData;
  message: string;
}
